import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { IZipCodeValidationType } from '@app/api/models/adress.models';

export const MIN_DESCRIPTION_CHARS = 0;
export const MAX_DESCRIPTION_CHARS = 750;

const NAME_FORBIDDEN_STARTING = ['hd'];

const emailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const siteRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;
const profileSiteRegex = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,99}(:[0-9]{1,99})?(\/.*)?$/;

const HTMLTagsRegex = /<\/?[\w\s="/.':;#-\/\?]+>/gi;

const URLRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

export class CustomValidatorProfile {
  static patternValidator(error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): Record<string, any> => {
      if (!control.value) {
        return null;
      }
      const valid = profileSiteRegex.test(control.value);

      return valid ? null : error;
    };
  }
}

export class CustomValidators {
  static patternValidator(error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): Record<string, any> => {
      if (!control.value) {
        return null;
      }
      const valid = siteRegex.test(`${control.value}`);

      return valid ? null : error;
    };
  }

  static descriptionWithUrl(): ValidatorFn {
    return (control: AbstractControl): Record<string, any> => {
      return URLRegex.exec(control.value) ? { invalidURL: { value: 'invalidURL' } } : null;
    };
  }

  /**
   * Custom validator to prevent leading and trailing spaces in the description field.
   */
  static noLeadingTrailingSpacesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';

      if (/^\s/.test(value)) {
        return { leadingSpace: true };
      }

      if (/\s$/.test(value)) {
        return { trailingSpace: true };
      }

      return null;
    };
  }

  static descriptionValidator(): ValidatorFn {
    return (control: AbstractControl): Record<string, any> => {
      const error: ValidationErrors = {
        validateDescription: {
          valid: false,
        },
      };

      let validator;

      if (control.value) {
        const description: string = control.value;
        const descriptionWithoutHTMLTags: string = description.replace(HTMLTagsRegex, '').trim();
        validator =
          descriptionWithoutHTMLTags.length >= MIN_DESCRIPTION_CHARS && descriptionWithoutHTMLTags.length <= MAX_DESCRIPTION_CHARS ? null : error;
      } else {
        validator = null;
      }
      return validator;
    };
  }

  /**
   * Custom validator to prevent multiple consecutive spaces between words in the description field.
   */
  static noMultipleSpacesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';

      if (/\s{2,}/.test(value)) {
        return { multipleSpaces: true };
      }

      return null;
    };
  }
}

function locatioNameValidator(c: AbstractControl): Record<string, any> {
  // values need to be set otherwise previous validator required fails
  const lowerName = c.value.toLowerCase();

  // check for names that are forbidden at the beginning of the name
  for (const part of NAME_FORBIDDEN_STARTING) {
    const subString = lowerName.substr(0, part.length);
    if (subString === part) {
      return {
        validateLocationName: {
          businessNameFilterError: true,
        },
      };
    }
  }
  return null;
}

export const LocationNameValidation = [Validators.required, locatioNameValidator];
export const EmailValidation = [Validators.pattern(emailRegex)];
export const PasswordValidation = [Validators.required, Validators.minLength(8), Validators.maxLength(50)];
export const ZipCodeValidation = [Validators.required, Validators.pattern(/^(?=.{2,10}$)[a-zA-Z0-9]+?[- ]{0,1}[a-zA-Z0-9]+$/)];
export const PhoneNumberValidation = [Validators.minLength(7), Validators.pattern(/^[0-9]*$/)];
export const PhoneNumberPrefixValidation = [Validators.pattern(/^\+?\d+$/)];
export const PhoneNumberWithPrefixValidation = [Validators.minLength(7), Validators.pattern(/^\+?[1-9]\d{1,50}$/)];

export const PhoneNumberPattern = [Validators.pattern('^[0-9]*$')];
const phoneNumberUtil = PhoneNumberUtil.getInstance();

export function PhoneNumberValidator(regionCode: string): ValidatorFn {
  return (control: AbstractControl): Record<string, any> => {
    const phoneNumber = control.value;
    if (!phoneNumber && phoneNumber !== 0) {
      return { wrongNumber: { value: '' } };
    }

    const auxNumber = regionCode + phoneNumber;
    try {
      const parsedNumber = phoneNumberUtil.parseAndKeepRawInput(auxNumber, null);
      const validNumber = phoneNumberUtil.isValidNumber(parsedNumber);
      return validNumber ? null : { wrongNumber: { value: phoneNumber } };
    } catch (e) {
      // phone number lib throws an error in case it's not valid
      return null;
    }
  };
}

export function URLValidator(url: string): string {
  const doc: HTMLInputElement = document.createElement('input');
  doc.type = 'url';
  doc.value = url;

  return doc.checkValidity() ? url : null;
}

export function UserDataValidation(error: string, gmbErrors: string): ValidatorFn {
  return (control: AbstractControl): Record<string, any> => {
    const invalidField = gmbErrors === error;
    return !invalidField ? null : { wrongField: { value: error } };
  };
}

export const zipCodeExpressions: IZipCodeValidationType = {
  AT: /^\d{4}$/,
  BE: /^\d{4}$/,
  CZ: /^\d{5}\s\(\d{3}\s\d{2}\)$/,
  DE: /^\d{5}$/,
  ES: /^\d{5}$/,
  FR: /^\d{5}$/,
  HR: /^\d{5}$/,
  HU: /^\d{4}$/,
  IT: /^\d{5}$/,
  NL: /^\d{4}\s{0,1}[A-Za-z]{2}$/,
  PL: /^\d{2}[- ]{0,1}\d{3}$/,
  PT: /^\d{4}[- ]{0,1}\d{3}$/,
  RO: /\d{6}$/,
  TR: /^\d{5}$/,
  UA: /^\d{5}$/,
};

export const defaultZipCodeExpression = /^(?=.{2,10}$)[a-zA-Z0-9]+?[- ]{0,1}[a-zA-Z0-9]+$/;
